import { useMemo } from 'react'
import useSelectedOrganisation from '@/state/entities/organisations/hooks/useSelectedOrganisation'
import useUser from '@/hooks/useUser'
import {
  filterVenuesByAccess,
  resolveAccessibleSerials,
} from '@/access/venue-access'

/**
 * The org's venues, narrowed to the ones the current user may reach.
 *
 * Filtering happens HERE, at the source, rather than in each picker. Every
 * switcher, select and venue grid reads through this hook, so they all inherit
 * the restriction by construction — which is the fix for the nav
 * `VenueSwitcher` happily offering every venue in the org to a user restricted
 * to one, while `venues/index.tsx` filtered the same list correctly with its own
 * copy of the logic.
 *
 * A user with no `location_access` rows is unrestricted (the normal case for
 * owners and admins), so for them this is a no-op.
 *
 * ⚠️ Presentation only. The API still serves venues the user was never shown;
 * backend enforcement is separate, deliberately-deferred work. Do not treat a
 * filtered picker as access control.
 */
const useLocations = () => {
  const organisation = useSelectedOrganisation()
  const user = useUser()

  const accessibleSerials = useMemo(
    () => resolveAccessibleSerials(user?.location_access),
    [user?.location_access]
  )

  return useMemo(() => {
    const sorted = [...(organisation?.locations ?? [])].sort((a, b) =>
      (a?.alias ?? '').localeCompare(b.alias ?? '')
    )
    return filterVenuesByAccess(sorted, accessibleSerials)
  }, [organisation, accessibleSerials])
}

/**
 * Alias of `useLocations` — also access-filtered.
 *
 * Kept as a separate export because ~30 call sites use one name or the other;
 * they are the same list. If a surface ever genuinely needs EVERY venue in the
 * org regardless of the current user (an org-admin audit view, say), add a
 * distinct `useEveryOrgLocation` rather than un-filtering this one.
 */
export const useAllLocations = () => {
  const locations = useLocations()

  return locations
}

/**
 * Every venue on the org, access filter and all.
 *
 * The one case the filtered list gets wrong: resolving a serial that
 * something else already holds. Square stores the venue a location is
 * mapped to as a serial, and looking that serial up in a list narrowed
 * to the current user answers "no such venue" for a mapping that is
 * perfectly valid, just to a venue they cannot open. The Square page
 * then offered to re-map it, so a restricted user could overwrite
 * another venue's mapping by accident.
 *
 * So: resolve names and existence through this, and keep offering
 * CHOICES from `useLocations`. Never use it to populate a picker.
 */
export const useEveryOrgLocation = () => {
  const organisation = useSelectedOrganisation()

  return useMemo(
    () =>
      [...(organisation?.locations ?? [])].sort((a, b) =>
        (a?.alias ?? '').localeCompare(b.alias ?? '')
      ),
    [organisation]
  )
}

/**
 * A single venue by serial, access-filtered.
 *
 * Returns undefined for a venue the user may not reach, so a hand-typed or
 * bookmarked serial resolves to "not found" rather than quietly rendering
 * another venue's data.
 */
const useLocation = (serial: string) => {
  const locations = useLocations()

  return useMemo(() => {
    return locations.find((location) => location.serial === serial)
  }, [locations, serial])
}

export const useVenueLocation = (serial: string) => {
  const location = useLocation(serial)

  return location
}

export default useLocations
